/**
 * Public Request-Demo page.
 *
 * Submits directly to the isolated **Demo v2** backend at
 * `https://demo.veralok.com/api/demo/request`. That backend runs on a
 * separate ECS task with a separate Mongo DB (`veralok_demo`) and
 * separate cryptographic secrets — it is the authoritative home for
 * every DEMO_INST_ID lifecycle event.
 *
 * The production API (`api.veralok.com`) has no `/api/demo/request`
 * endpoint (the demo router is only registered when
 * `VLK_RUNTIME_MODE=demo`), so submitting here to the demo host is
 * both the correct architectural boundary and the reason we do not
 * fall back to a marketing "lead" table on prod.
 *
 * Response semantics:
 *   200 → `{request_id, state, tenancy, message, routed_to}`.
 *   422 → validation error (invalid classification, etc.).
 *
 * On success the form displays the Demo v2 confirmation screen with
 * the operator-review notice, request_id and the isolation reminder.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import VeraLokLogo from "@/marketing/VeraLokLogo";

const DEMO_HOST = "https://demo.veralok.com";

const CLASSIFICATIONS = [
  "Hospitality",
  "Enterprise Services",
  "Health",
  "Finance",
  "Government",
];

export default function RequestDemoPage() {
  const [form, setForm] = useState({
    organisation: "",
    representative_name: "",
    representative_email: "",
    industry_classification: "",
    use_case: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${DEMO_HOST}/api/demo/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(
          body?.detail?.reason_code === "invalid_classification"
            ? "Please choose a supported institutional classification."
            : "We couldn't submit your request. Please try again in a moment."
        );
      } else {
        setResult(body);
      }
    } catch (_err) {
      setError(
        "We couldn't reach the Demo intake. Please try again shortly."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <Link to="/" data-testid="rd-home-link">
          <VeraLokLogo size={30} />
        </Link>
        <Link
          to="/"
          className="text-sm text-slate-400 hover:text-slate-100"
          data-testid="rd-back-home"
        >
          ← Back to home
        </Link>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10">
        {!result ? (
          <>
            <div className="mb-3 inline-block rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs uppercase tracking-widest text-amber-300">
              Demo · Synthetic · Non-Production
            </div>
            <h1 className="text-4xl font-semibold tracking-tight">
              Request a VeraLok demo
            </h1>
            <p className="mt-4 max-w-xl text-slate-300">
              See VeraLok's institutional workflows on isolated synthetic
              data. A VeraLok operator will review your request and email
              your 7-day evaluator credentials.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="rd-form">
              <Field label="Organisation" value={form.organisation}
                     onChange={update("organisation")} required min={2} max={200}
                     placeholder="Sample Bank Ltd" testId="rd-organisation" />
              <Field label="Your name" value={form.representative_name}
                     onChange={update("representative_name")} required min={2} max={200}
                     placeholder="Jane Doe" testId="rd-name" />
              <Field label="Work email" type="email"
                     value={form.representative_email}
                     onChange={update("representative_email")} required
                     placeholder="[email protected]" testId="rd-email" />
              <div>
                <label className="mb-2 block text-xs uppercase tracking-widest text-slate-400">
                  Institutional classification
                </label>
                <select
                  value={form.industry_classification}
                  onChange={update("industry_classification")}
                  required
                  className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-slate-100 focus:border-slate-500 focus:outline-none"
                  data-testid="rd-classification"
                >
                  <option value="">Choose one…</option>
                  {CLASSIFICATIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-xs uppercase tracking-widest text-slate-400">
                  Use case — what would you like to see?
                </label>
                <textarea
                  value={form.use_case}
                  onChange={update("use_case")}
                  required
                  minLength={10}
                  maxLength={500}
                  rows={5}
                  className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-slate-100 focus:border-slate-500 focus:outline-none"
                  placeholder="E.g. how KYC + age-verification works for our onboarding flow…"
                  data-testid="rd-usecase"
                />
              </div>

              {error && (
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
                     data-testid="rd-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="rounded-full bg-sky-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-500/20 transition hover:bg-sky-400 disabled:opacity-50"
                data-testid="rd-submit"
              >
                {busy ? "Submitting…" : "Request demo"}
              </button>

              <p className="mt-6 text-xs text-slate-500">
                Access is valid for 7 days once activated. This Demo
                environment operates on isolated synthetic data —
                evaluator credentials cannot be converted or promoted to
                production status.
              </p>
            </form>
          </>
        ) : (
          <div data-testid="rd-success">
            <div className="mb-3 inline-block rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs uppercase tracking-widest text-emerald-300">
              Received
            </div>
            <h1 className="text-4xl font-semibold tracking-tight">
              Thanks — your demo request is with us.
            </h1>
            <p className="mt-4 text-slate-300">
              A VeraLok operator will review your request within one
              business day and email your temporary evaluator credentials.
            </p>
            <div className="mt-6 rounded-lg border border-slate-800 bg-slate-900 p-4 text-sm text-slate-300">
              <div className="text-xs uppercase tracking-widest text-slate-500">Reference</div>
              <div className="mt-1 font-mono text-slate-100" data-testid="rd-request-id">
                {result.request_id}
              </div>
              {result.routed_to && (
                <>
                  <div className="mt-3 text-xs uppercase tracking-widest text-slate-500">Routed to</div>
                  <div className="mt-1 font-mono text-slate-100">{result.routed_to}</div>
                </>
              )}
            </div>
            <p className="mt-4 text-sm text-slate-400">
              This Demo environment uses isolated synthetic (non-production) data —
              evaluator credentials cannot be converted to production status.
            </p>
            <Link
              to="/"
              className="mt-8 inline-block rounded-full border border-slate-700 px-5 py-2 text-sm text-slate-200 hover:bg-slate-900"
              data-testid="rd-back-home-success"
            >
              Back to home
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, onChange, required, min, max, type = "text",
                 placeholder, testId }) {
  return (
    <div>
      <label className="mb-2 block text-xs uppercase tracking-widest text-slate-400">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        minLength={min}
        maxLength={max}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-slate-100 focus:border-slate-500 focus:outline-none"
        data-testid={testId}
      />
    </div>
  );
}
